/** Apple Calendar handoff — decides whether a "Save to Kin + Apple Calendar"
 *  tap has earned an .ics link yet, and builds the URL that link points at.
 *
 *  ── Why this is its own module ────────────────────────────────────────────
 *
 *  Two bugs shipped together on the combined button, and both of them lived
 *  in the ORDER the component did things, which is exactly the kind of thing
 *  that's invisible in a diff and untestable inside a 1300-line component:
 *
 *  1. The .ics handoff fired BEFORE the save. f7dc0c4 (Jul 13) moved it there
 *     deliberately — WKWebView only honors window.open() while it's still in
 *     the same synchronous tick as the tap, so the old post-await call was
 *     silently popup-blocked. The cost of that workaround was that the
 *     handoff could strand the webview while the save was still in flight,
 *     and a force-quit in that window lost the whole batch. It was a race,
 *     not a guaranteed loss (Aug 3: the write won, 4 rows landed), which is
 *     precisely why it survived review.
 *
 *  2. The handoff itself was a `data:text/calendar` URI, which Median's
 *     WKWebView intercepts as a generic download. See
 *     supabase/functions/schedule-ics/index.ts for that whole history.
 *
 *  Fixing (2) dissolves (1): a real https URL doesn't need a popup, so the
 *  handoff can be an ordinary link the user taps AFTER the save has already
 *  been awaited to completion. No gesture deadline, nothing to race.
 *
 *  `resolveAppleHandoff` is the gate. It is the only thing that can produce
 *  a URL, and it cannot produce one from a failed save — that's the property
 *  src/components/__tests__/scheduleAppleHandoff.test.tsx pins down.
 */

/** Mirrors ReminderValue in src/components/SchedulePreview.tsx. */
export type AppleReminderValue = "none" | "0" | "15" | "30" | "60" | "1440" | "4320" | "10080";

/** Path served by the vercel.json rewrite → schedule-ics Edge Function. */
export const SCHEDULE_ICS_PATH = "/ics";

export interface ScheduleIcsUrlInput {
  /** batch_id of the rows insertKinEvents just wrote. Null when the save was
   *  an all-duplicates no-op (insertKinEvents returns 0 without a DB write,
   *  so no row carries this batch). */
  batchId: string | null;
  /** imported_events ids of dedup-skipped rows from EARLIER imports. They're
   *  real events she selected, just not re-inserted, and the old client-side
   *  export handed them off too — dropping them here would be a silent
   *  behavior change. */
  alsoIds?: string[];
  reminder: AppleReminderValue;
  /** Defaults to window.location.origin. Injectable for tests. */
  origin?: string;
}

export type AppleHandoff =
  /** Save landed and there's something to hand off. Safe to render a link. */
  | { kind: "ready"; url: string }
  /** Save landed but there was nothing to export. No link, no error. */
  | { kind: "none" }
  /** Save did NOT land. Never navigate — she'd be sent to a calendar file for
   *  events that aren't in Kin. */
  | { kind: "blocked" };

function resolveOrigin(explicit?: string): string {
  if (explicit) return explicit;
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return "";
}

/** Builds the /ics URL for a saved batch. Returns null when there's nothing
 *  addressable — no batch and no also-ids means no rows to serve. */
export function buildScheduleIcsUrl(input: ScheduleIcsUrlInput): string | null {
  const alsoIds = (input.alsoIds ?? []).filter(Boolean);
  if (!input.batchId && alsoIds.length === 0) return null;

  const params = new URLSearchParams();
  if (input.batchId) params.set("batch", input.batchId);
  if (alsoIds.length > 0) params.set("also", alsoIds.join(","));
  // Always explicit, even for "none" — the Edge Function defaults unknown
  // values to "none", and being explicit makes the URL self-describing in
  // logs when a reminder doesn't come through.
  params.set("reminder", input.reminder);

  return `${resolveOrigin(input.origin)}${SCHEDULE_ICS_PATH}?${params.toString()}`;
}

export interface ResolveAppleHandoffArgs {
  /** Did the Save to Kin write run to completion without throwing? */
  saveSucceeded: boolean;
  /** Rows freshly inserted under `batchId`. */
  inserted: number;
  /** Rows the dedup RPC auto-skipped as already-saved duplicates. */
  skipped: number;
  batchId: string | null;
  alsoIds?: string[];
  reminder: AppleReminderValue;
  origin?: string;
}

/** The gate. A failed save can only ever produce "blocked". */
export function resolveAppleHandoff(args: ResolveAppleHandoffArgs): AppleHandoff {
  if (!args.saveSucceeded) return { kind: "blocked" };
  if (args.inserted === 0 && args.skipped === 0) return { kind: "none" };

  // Nothing was newly inserted, so no row carries this batch_id — the export
  // has to come from the skipped rows alone.
  const batchId = args.inserted > 0 ? args.batchId : null;
  const url = buildScheduleIcsUrl({
    batchId,
    alsoIds: args.alsoIds,
    reminder: args.reminder,
    origin: args.origin,
  });
  return url ? { kind: "ready", url } : { kind: "none" };
}

/** The imported_events row a dedup-skipped candidate actually matched — an
 *  exact match wins, otherwise the first reported match.
 *
 *  Mirrors the rule the Google push branch applies inline in
 *  SchedulePreview's finishSave (`skippedRowIds`). Deliberately NOT swapped
 *  in there as part of this change: that branch is out of scope for this fix
 *  and a shared helper can wait for a change that's actually touching it.
 */
export function skippedMatchRowIds(
  skippedResults: Array<{ matches: Array<{ id: string; match_type: string }> }>
): string[] {
  return Array.from(
    new Set(
      skippedResults
        .map((r) => r.matches.find((m) => m.match_type === "exact")?.id ?? r.matches[0]?.id)
        .filter((id): id is string => Boolean(id))
    )
  );
}
